import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    getTeachingCourseDetail,
    getCourseStats,
    createFolder,
    deleteFolder,
    addSentences,
    deleteSentence,
    deleteCourse,
    courseErrorMessage,
} from "../courses";

const LANGUAGE_META = {
    "de-DE": { flag: "🇩🇪", labelKey: "German" },
    "en-GB": { flag: "🇬🇧", labelKey: "English" },
    "fr-FR": { flag: "🇫🇷", labelKey: "French" },
};

const scoreBadgeClass = (score) => {
    if (score === null || score === undefined) return "bg-gray-100 text-gray-500";
    if (score >= 85) return "bg-green-100 text-green-800";
    if (score >= 65) return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
};

const ScoreBadge = ({ score, t }) => (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${scoreBadgeClass(score)}`}>
        {score !== null && score !== undefined ? `${score} ${t("unit.points")}` : "–"}
    </span>
);

const FolderIcon = (props) => (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" {...props}>
        <path d="M2 5a2 2 0 0 1 2-2h3.17a2 2 0 0 1 1.42.59L9.83 4.8a1 1 0 0 0 .7.29H16a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5Z" />
    </svg>
);

const nonEmptyLines = (text) =>
    text.split("\n").map((s) => s.trim()).filter(Boolean);

// Inline "add sentences" box that lives inside each folder (and the unfiled
// bucket), so sentences are typed exactly where they will end up — no separate
// target picker to reason about.
const SentenceComposer = ({ value, onChange, onSubmit, saving, t }) => {
    const count = nonEmptyLines(value).length;
    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
            }}
            className="mt-3 flex flex-col gap-2"
        >
            <textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={t("teach.newSentencesPlaceholder")}
                rows={2}
                className="rounded-md border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
                type="submit"
                disabled={saving || count === 0}
                className="self-end rounded-md border border-transparent bg-primary px-4 py-2 text-sm text-white hover:bg-purple-600 disabled:opacity-50"
            >
                {count > 0
                    ? t("teach.addSentences", { count })
                    : t("teach.addSentencesEmpty")}
            </button>
        </form>
    );
};

// Teacher management view of one course: join code, folder/sentence editing,
// and aggregated student performance.
const TeachCoursePage = () => {
    const { courseId } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [course, setCourse] = useState(null);
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);
    const [shareCopied, setShareCopied] = useState(false);

    const [newFolderName, setNewFolderName] = useState("");
    const [showNewFolder, setShowNewFolder] = useState(false);
    // Sentence drafts keyed by section ("unfiled" or a folder id), so each
    // folder's composer keeps its own in-progress text.
    const [drafts, setDrafts] = useState({});
    const [savingKey, setSavingKey] = useState(null);

    const setDraft = (key, value) => setDrafts((d) => ({ ...d, [key]: value }));

    const loadAll = useCallback(() => {
        getTeachingCourseDetail(courseId)
            .then(setCourse)
            .catch((err) => setError(courseErrorMessage(err, t)));
        getCourseStats(courseId)
            .then(setStats)
            .catch((err) => console.error("Failed to load stats:", err));
    }, [courseId]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const handleCopyCode = () => {
        navigator.clipboard.writeText(course.join_code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleCopyShareLink = () => {
        const shareLink = `${window.location.origin}/c/${course.join_code}`;
        navigator.clipboard.writeText(shareLink).then(() => {
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2000);
        });
    };

    const handleAddFolder = (e) => {
        e.preventDefault();
        if (!newFolderName.trim()) return;
        createFolder(courseId, newFolderName.trim())
            .then(() => {
                setNewFolderName("");
                setShowNewFolder(false);
                loadAll();
            })
            .catch((err) => setError(courseErrorMessage(err, t)));
    };

    // Add the draft sentences for one section into its folder (folderId null
    // for the unfiled bucket).
    const handleAddSentences = (key, folderId) => {
        const sentences = nonEmptyLines(drafts[key] || "");
        if (sentences.length === 0 || savingKey) return;
        setSavingKey(key);
        addSentences(courseId, sentences, folderId)
            .then(() => {
                setDraft(key, "");
                loadAll();
            })
            .catch((err) => setError(courseErrorMessage(err, t)))
            .finally(() => setSavingKey(null));
    };

    const handleDeleteSentence = (sentenceId) => {
        deleteSentence(courseId, sentenceId).then(loadAll).catch((err) => setError(courseErrorMessage(err, t)));
    };

    const handleDeleteFolder = (folderId) => {
        if (!window.confirm(t("teach.confirmDeleteFolder"))) return;
        deleteFolder(courseId, folderId).then(loadAll).catch((err) => setError(courseErrorMessage(err, t)));
    };

    const handleDeleteCourse = () => {
        if (!window.confirm(t("teach.confirmDeleteCourse"))) return;
        deleteCourse(courseId)
            .then(() => navigate("/teach"))
            .catch((err) => setError(courseErrorMessage(err, t)));
    };

    if (!course) {
        return (
            <div className="px-4 pt-8 md:px-20">
                {error ? (
                    <p className="text-red-600">{error}</p>
                ) : (
                    <div className="h-32 animate-pulse rounded bg-gray-200" />
                )}
            </div>
        );
    }

    // Group sentences by their folder id (or "unfiled") for display.
    const sentencesByFolder = new Map();
    course.sentences.forEach((sentence) => {
        const key = sentence.folder ?? "unfiled";
        if (!sentencesByFolder.has(key)) sentencesByFolder.set(key, []);
        sentencesByFolder.get(key).push(sentence);
    });

    // One card per folder, each with its own inline sentence composer, plus an
    // always-present "unfiled" bucket for loose sentences.
    const folderSections = course.folders.map((folder) => ({
        key: String(folder.id),
        folderId: folder.id,
        name: folder.name,
        isUnfiled: false,
        sentences: sentencesByFolder.get(folder.id) || [],
    }));
    const unfiledSection = {
        key: "unfiled",
        folderId: null,
        name: t("courses.unfiled"),
        isUnfiled: true,
        sentences: sentencesByFolder.get("unfiled") || [],
    };

    const formatDate = (value) =>
        value ? new Date(value).toLocaleDateString() : "–";

    const renderSection = (section) => (
        <div
            key={section.key}
            className={`rounded-lg border p-4 md:p-6 ${
                section.isUnfiled
                    ? "border-dashed border-border bg-gray-50"
                    : "border-border bg-secondary"
            }`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="flex items-center gap-2 font-medium">
                        {section.isUnfiled ? (
                            <span className="text-gray-600">{section.name}</span>
                        ) : (
                            <>
                                <FolderIcon className="h-4 w-4 shrink-0 text-primary" />
                                <span className="truncate">{section.name}</span>
                            </>
                        )}
                        <span className="text-xs font-normal text-gray-400">
                            ({section.sentences.length})
                        </span>
                    </h3>
                    {section.isUnfiled && (
                        <p className="mt-0.5 text-xs text-gray-500">
                            {t("teach.unfiledHint")}
                        </p>
                    )}
                </div>
                {!section.isUnfiled && (
                    <button
                        className="shrink-0 text-xs text-red-600 hover:underline"
                        onClick={() => handleDeleteFolder(section.folderId)}
                    >
                        {t("teach.deleteFolder")}
                    </button>
                )}
            </div>

            {section.sentences.length > 0 && (
                <ul className="mt-3 flex flex-col gap-2">
                    {section.sentences.map((sentence) => (
                        <li
                            key={sentence.id}
                            className="flex items-center justify-between gap-3 rounded-md border border-border bg-white p-3"
                        >
                            <span className="min-w-0">{sentence.text}</span>
                            <button
                                className="shrink-0 text-xs text-red-600 hover:underline"
                                onClick={() => handleDeleteSentence(sentence.id)}
                            >
                                {t("teach.remove")}
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <SentenceComposer
                value={drafts[section.key] || ""}
                onChange={(value) => setDraft(section.key, value)}
                onSubmit={() => handleAddSentences(section.key, section.folderId)}
                saving={savingKey === section.key}
                t={t}
            />
        </div>
    );

    return (
        <div className="px-4 pt-4 md:px-20 md:pt-10 flex flex-col gap-4 pb-10">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <Link to="/teach" className="text-sm text-gray-500 hover:underline">
                        ← {t("teach.title")}
                    </Link>
                    <h1 className="mt-1 text-2xl md:text-3xl font-semibold">{course.title}</h1>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <button
                        className="text-sm text-red-600 hover:underline"
                        onClick={handleDeleteCourse}
                    >
                        {t("teach.deleteCourse")}
                    </button>
                    <span className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-sm font-medium text-gray-700">
                        <span className="text-base leading-none">
                            {(LANGUAGE_META[course.language] || {}).flag}
                        </span>
                        {t((LANGUAGE_META[course.language] || {}).labelKey || course.language)}
                    </span>
                </div>
            </div>

            {/* Join code */}
            <div className="rounded-lg border border-border bg-secondary p-4 md:p-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <p className="text-sm text-gray-600">{t("teach.joinCodeHint")}</p>
                    <p className="mt-1 font-mono text-3xl font-bold tracking-[0.3em] text-primary">
                        {course.join_code}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        className="rounded-md border border-border bg-white px-4 py-2 text-sm hover:bg-gray-100"
                        onClick={handleCopyCode}
                    >
                        {copied ? t("embedGenerator.copied") : t("teach.copyCode")}
                    </button>
                    <button
                        className="rounded-md border border-border bg-white px-4 py-2 text-sm hover:bg-gray-100"
                        onClick={handleCopyShareLink}
                    >
                        {shareCopied ? t("teach.shareLinkCopied") : t("teach.copyShareLink")}
                    </button>
                </div>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            {/* Course content: folders (each with its own sentence composer)
                followed by the unfiled bucket. */}
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <h2 className="text-lg font-semibold">{t("teach.courseContent")}</h2>
                        <p className="text-sm text-gray-500">{t("teach.contentHint")}</p>
                    </div>
                    {!showNewFolder && (
                        <button
                            onClick={() => setShowNewFolder(true)}
                            className="rounded-md border border-border bg-white px-4 py-2 text-sm hover:bg-gray-100"
                        >
                            + {t("teach.newFolderShort")}
                        </button>
                    )}
                </div>

                {showNewFolder && (
                    <form
                        onSubmit={handleAddFolder}
                        className="rounded-lg border border-dashed border-primary/40 bg-secondary p-4 flex items-end gap-2 flex-wrap"
                    >
                        <div className="flex flex-col gap-1 grow max-w-xs">
                            <label htmlFor="new-folder" className="text-sm text-gray-600">
                                {t("teach.newFolder")}
                            </label>
                            <input
                                id="new-folder"
                                type="text"
                                autoFocus
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                placeholder={t("teach.newFolderPlaceholder")}
                                className="rounded-md border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={!newFolderName.trim()}
                            className="rounded-md border border-transparent bg-primary px-4 py-2 text-sm text-white hover:bg-purple-600 disabled:opacity-50"
                        >
                            {t("teach.addFolder")}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowNewFolder(false);
                                setNewFolderName("");
                            }}
                            className="rounded-md border border-border bg-white px-4 py-2 text-sm hover:bg-gray-100"
                        >
                            {t("teach.cancel")}
                        </button>
                    </form>
                )}

                {folderSections.map(renderSection)}
                {renderSection(unfiledSection)}
            </div>

            {/* Student performance */}
            <div className="rounded-lg border border-border bg-secondary p-4 md:p-6">
                <h2 className="font-medium">{t("teach.studentProgress")}</h2>
                {stats === null ? (
                    <div className="mt-3 h-16 animate-pulse rounded bg-gray-200" />
                ) : (
                    <>
                        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-700">
                            <span>
                                {t("teach.studentsTile")}: {stats.totals.student_count}
                            </span>
                            <span>
                                {t("teach.totalAttempts")}: {stats.totals.total_attempts}
                            </span>
                            <span>
                                {t("courses.averageScore")}:{" "}
                                {stats.totals.average_score ?? "–"}
                            </span>
                            <span>
                                {t("teach.activeLast7Days")}:{" "}
                                {stats.totals.active_students_last_7_days}
                            </span>
                        </div>

                        <div className="mt-4 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-gray-500 border-b border-border">
                                        <th className="py-2 pr-4 font-medium">{t("auth.email")}</th>
                                        <th className="py-2 pr-4 font-medium">{t("teach.progress")}</th>
                                        <th className="py-2 pr-4 font-medium">{t("teach.attempts")}</th>
                                        <th className="py-2 pr-4 font-medium">{t("courses.averageScore")}</th>
                                        <th className="py-2 font-medium">{t("teach.lastActive")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.students.map((student) => (
                                        <tr key={student.user_id} className="border-b border-border/50">
                                            <td className="py-2 pr-4">{student.email}</td>
                                            <td className="py-2 pr-4">
                                                {student.practiced_sentences}/{student.total_sentences}
                                            </td>
                                            <td className="py-2 pr-4">{student.attempts}</td>
                                            <td className="py-2 pr-4">
                                                <ScoreBadge score={student.average_score} t={t} />
                                            </td>
                                            <td className="py-2">{formatDate(student.last_active)}</td>
                                        </tr>
                                    ))}
                                    {stats.students.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="py-3 text-gray-500">
                                                {t("teach.noStudentsYet")}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {/* Per-sentence performance */}
            {stats !== null && stats.sentences.length > 0 && (
                <div className="rounded-lg border border-border bg-secondary p-4 md:p-6">
                    <h2 className="font-medium">{t("teach.sentencePerformance")}</h2>
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-gray-500 border-b border-border">
                                    <th className="py-2 pr-4 font-medium">{t("courses.sentence")}</th>
                                    <th className="py-2 pr-4 font-medium">{t("teach.folder")}</th>
                                    <th className="py-2 pr-4 font-medium">{t("teach.studentsPracticed")}</th>
                                    <th className="py-2 pr-4 font-medium">{t("teach.attempts")}</th>
                                    <th className="py-2 font-medium">{t("courses.averageScore")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.sentences.map((sentence) => (
                                    <tr key={sentence.id} className="border-b border-border/50">
                                        <td className="py-2 pr-4">{sentence.text}</td>
                                        <td className="py-2 pr-4">{sentence.folder ?? "–"}</td>
                                        <td className="py-2 pr-4">{sentence.students_practiced}</td>
                                        <td className="py-2 pr-4">{sentence.attempts}</td>
                                        <td className="py-2">
                                            <ScoreBadge score={sentence.average_score} t={t} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeachCoursePage;
